import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from '../plannerApi';
import { PROJECT_STATUSES, PRIORITIES } from '../plannerConstants';
import PeopleSelect from './PeopleSelect';

const EMPTY = { name: '', description: '', owner_employee_id: null, member_employee_ids: [], start_date: '', end_date: '', status: 'active', priority: 'medium' };

// iter_153 · Batch 3 — when opened from My Projects the caller doesn't know
// which department the new project should belong to. `departmentId` is now
// optional; when null we render an inline department picker and require a
// selection before enabling the save button. Existing callers (Dept dashboard,
// Project board) still pass a fixed `departmentId` and behave exactly as
// before — the picker is hidden in that case.
export default function ProjectFormDialog({ open, onClose, departmentId, project, people = [], onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [pickedDept, setPickedDept] = useState(departmentId || '');
  const [deptOptions, setDeptOptions] = useState([]);
  const isEdit = !!project;
  const needsDeptPicker = !departmentId && !isEdit;

  useEffect(() => {
    if (!open) return;
    if (project) setForm({ ...EMPTY, ...project, start_date: project.start_date || '', end_date: project.end_date || '', owner_employee_id: project.owner_employee_id || null, member_employee_ids: project.member_employee_ids || [],
      // The stored value is kept VERBATIM. An earlier attempt clamped it to
      // 'active', which would have silently rewritten a historical
      // status='completed' on the next save — a write to an existing record,
      // not a display fix. The legacy value is offered as an option instead
      // (see statusOptions below) so the control is never blank either.
      status: project.status || 'active' });
    else setForm(EMPTY);
    setPickedDept(departmentId || '');
    setErrors({});
  }, [open, project, departmentId]);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('planner.project.nameRequired');
    if (!isEdit && !pickedDept) next.dept = t('planner.myProjects.deptRequired');
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      next.end_date = t('planner.dateOrder');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // 'completed' is no longer選択可能 (finished is `archived`), but a record
  // stored before that change may still hold it. Include whatever the record
  // actually has, so the trigger renders and nothing is silently rewritten.
  const statusOptions = PROJECT_STATUSES.includes(form.status)
    ? PROJECT_STATUSES : [...PROJECT_STATUSES, form.status];

  // Lazy-load department list only when the picker is actually needed.
  useEffect(() => {
    if (!open || !needsDeptPicker) return;
    plannerApi.departments().then(setDeptOptions).catch(() => setDeptOptions([]));
  }, [open, needsDeptPicker]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!validate()) { toast.error(t('planner.checkFields')); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(), description: form.description || '',
        owner_employee_id: form.owner_employee_id || null,
        member_employee_ids: form.member_employee_ids || [],
        start_date: form.start_date || null, end_date: form.end_date || null,
        status: form.status, priority: form.priority,
      };
      let created;
      if (isEdit) created = await plannerApi.updateProject(project.id, body);
      else created = await plannerApi.createProject({ department_id: pickedDept, ...body });
      toast.success(t('planner.saved'));
      onSaved?.(created);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="project-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? t('planner.project.edit') : t('planner.project.new')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {needsDeptPicker && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('planner.myProjects.deptLabel')} *</Label>
              <Select value={pickedDept || undefined} onValueChange={(v) => setPickedDept(v)}>
                <SelectTrigger data-testid="project-department-select"
                  className={`h-10 mt-1 ${errors.dept ? 'border-rose-400 ring-1 ring-rose-200' : ''}`}>
                  <SelectValue placeholder={t('planner.myProjects.deptPh')} />
                </SelectTrigger>
                <SelectContent>
                  {deptOptions.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`project-department-option-${d.id}`}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.name')} *</Label>
            <Input data-testid="project-name-input" value={form.name} onChange={set('name')} aria-invalid={!!errors.name}
              className={`h-10 mt-1 ${errors.name ? 'border-rose-400 ring-1 ring-rose-200' : ''}`} />
            {errors.name && <p className="mt-1 text-[11px] text-rose-600" data-testid="project-name-error">{errors.name}</p>}
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.description')}</Label>
            <Textarea data-testid="project-desc-input" value={form.description || ''} onChange={set('description')} rows={2} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.owner')}</Label>
              <PeopleSelect testid="project-owner" value={form.owner_employee_id} people={people}
                onChange={(v) => setForm((p) => ({ ...p, owner_employee_id: v }))} placeholder={t('planner.people.none')} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.priority')}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
                <SelectTrigger data-testid="project-priority-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`planner.priority.${p}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.status')}</Label>
            <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
              <SelectTrigger data-testid="project-status-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{statusOptions.map((s) => <SelectItem key={s} value={s} data-testid={`project-status-option-${s}`}>{t(`planner.projectStatus.${s}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.members')}</Label>
            <PeopleSelect testid="project-members" multi value={form.member_employee_ids} people={people}
              onChange={(v) => setForm((p) => ({ ...p, member_employee_ids: v }))} placeholder={t('planner.project.membersPh')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.start')}</Label>
              <DateField testId="project-start-input" value={form.start_date || ''} onChange={set('start_date')} className="mt-1" inputClassName="h-10" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('planner.project.end')}</Label>
              <DateField testId="project-end-input" value={form.end_date || ''} onChange={set('end_date')} className="mt-1"
                inputClassName={`h-10 ${errors.end_date ? 'border-rose-400 ring-1 ring-rose-200' : ''}`} />
              {errors.end_date && <p className="mt-1 text-[11px] text-rose-600" data-testid="project-end-error">{errors.end_date}</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('planner.cancel')}</Button>
          <Button onClick={save} disabled={busy} data-testid="project-save-btn" className="bg-teal-700 hover:bg-teal-800">{busy ? t('planner.saving') : t('planner.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
